/**
 * The local database.
 *
 * IndexedDB via Dexie. Defined in full now, in Phase 0b, even though only
 * `syncMeta` is written before Phase 5 — because the schema is the seam. When the
 * repositories switch from pass-through-to-API to local-first, the tables they
 * need already exist with the right indexes, and no screen changes.
 *
 * Two rules this file exists to enforce:
 *
 *   1. **A controlled subset, never a mirror of the central database.** Only what
 *      a terminal needs to trade: its section's products, current prices, a stock
 *      snapshot, recently-used customers. Not the full customer base — that is
 *      POPIA data minimisation, not just disk economy.
 *
 *   2. **Nothing is deleted because a request was sent.** Local sales survive
 *      until the server acknowledges them AND a retention window has passed, so a
 *      cashier can still reprint yesterday's receipt with no connectivity.
 *
 * See docs/ARCHITECTURE.md §4.2–4.4.
 */

import Dexie, { type EntityTable, type Table } from "dexie";

import type {
  HeldSale,
  LocalCategory,
  LocalCustomer,
  LocalPayment,
  LocalProduct,
  LocalSale,
  LocalSettings,
  LocalShift,
  LocalStockMovement,
  LocalStockSnapshot,
  OutboxOperation,
  SyncConflict,
  SyncLogEntry,
  LocalRefund,
  OfflineGrant,
  StoredSession,
  SyncMeta,
} from "./schema";

/** Bumped whenever the schema below changes. Dexie migrates on open. */
export const SCHEMA_VERSION = 6;

export class SmartSpazaDatabase extends Dexie {
  // ── Replicated reference data (server-authoritative, read-only here) ───────
  products!: EntityTable<LocalProduct, "id">;
  categories!: EntityTable<LocalCategory, "id">;
  stockLevels!: Table<LocalStockSnapshot, [string, string]>;
  customers!: EntityTable<LocalCustomer, "id">;
  settings!: EntityTable<LocalSettings, "key">;

  // ── Device-originated, held until acknowledged ─────────────────────────────
  pendingSales!: EntityTable<LocalSale, "id">;
  pendingPayments!: EntityTable<LocalPayment, "id">;
  pendingStockMovements!: EntityTable<LocalStockMovement, "id">;
  heldSales!: EntityTable<HeldSale, "id">;
  shifts!: EntityTable<LocalShift, "id">;

  // ── Session ───────────────────────────────────────────────────────────────
  auth!: EntityTable<StoredSession, "key">;
  offlineGrants!: EntityTable<OfflineGrant, "userId">;
  pendingRefunds!: EntityTable<LocalRefund, "id">;

  // ── Sync machinery ────────────────────────────────────────────────────────
  outbox!: EntityTable<OutboxOperation, "opId">;
  syncMeta!: EntityTable<SyncMeta, "key">;
  conflicts!: EntityTable<SyncConflict, "id">;
  syncLog!: EntityTable<SyncLogEntry, "id">;

  constructor(name = "smartspaza") {
    super(name);

    this.version(1).stores({
      // `*barcodes` is a multi-entry index: one product may carry several
      // barcodes, and the POS hot path is barcode → product in one lookup.
      products: "id, name, sku, *barcodes, categoryId, isActive, updatedAt",
      categories: "id, sortOrder",
      stockSnapshot: "productId, sectionId, [sectionId+productId]",
      customers: "id, name, phone, lastUsedAt",
      settings: "key",

      // Compound [syncState+businessDate] so the sync engine can claim work and
      // the retention sweep can find acknowledged sales older than the window,
      // both without a table scan.
      pendingSales: "id, receiptNo, businessDate, syncState, [syncState+businessDate]",
      pendingPayments: "id, saleId, syncState",
      pendingStockMovements: "id, productId, businessDate, syncState",
      heldSales: "id, createdAt",

      // `[state+nextAttemptAt]` is the queue's working index: "give me PENDING
      // operations whose backoff has elapsed". `sequence` preserves causal order
      // so shift.open is always uploaded before the sales that depend on it.
      outbox: "opId, type, entityId, state, sequence, nextAttemptAt, [state+nextAttemptAt]",
      syncMeta: "key",
      conflicts: "id, opId, entityType, resolvedAt",
      syncLog: "++id, at",
    });

    // v2 adds the session table. Declared as its own version rather than folded
    // into v1 so a terminal already running v1 upgrades in place instead of
    // being recreated — which would mean discarding its outbox, and with it any
    // unsynced sales.
    this.version(2).stores({ auth: "key" });

    // v3 adds the shift this terminal is on. Same reasoning as v2: its own
    // version, so a till already carrying unsynced sales upgrades in place.
    // `[tillId+status]` is the till bar's question — "is a shift open on
    // this till?" — asked on every load and every sale.
    this.version(3).stores({ shifts: "id, tillId, status, [tillId+status]" });

    // v4 adds offline sign-in grants. Keyed by user, looked up by the staff
    // code the cashier types.
    this.version(4).stores({ offlineGrants: "userId, staffCode" });

    // v5 adds refunds given offline, looked up by the sale they reverse.
    this.version(5).stores({ pendingRefunds: "id, saleId" });

    // v6 keys local stock by counter and product. `stockSnapshot` was keyed by
    // product alone, so a till selling for two counters kept one figure and the
    // second overwrote the first. A primary key cannot change in place, so the
    // rows move to a new table — pendingDelta and all, because that is this
    // till's unsent sales — and the old one goes.
    this.version(SCHEMA_VERSION)
      .stores({
        stockLevels: "[sectionId+productId], productId, sectionId",
        stockSnapshot: null,
      })
      .upgrade(async (tx) => {
        const rows = await tx.table<LocalStockSnapshot>("stockSnapshot").toArray();
        await tx
          .table<LocalStockSnapshot>("stockLevels")
          .bulkPut(rows.filter((row) => row.sectionId));
      });
  }

  /**
   * Wipe every table.
   *
   * For device de-registration and for tests. Deliberately **not** exposed in the
   * interface: a cashier must never be one mis-tap away from discarding unsynced
   * sales. Phase 6 gates the equivalent action behind a check that the outbox is
   * empty.
   */
  async wipe(): Promise<void> {
    await this.transaction("rw", this.tables, async () => {
      await Promise.all(this.tables.map((table) => table.clear()));
    });
  }

  /**
   * Forget every shop's cached data but keep who is signed in.
   *
   * For SmartSpaza support leaving a shop they were viewing: an admin's browser
   * has no business keeping a copy of somebody's sales and customers. Never
   * called on a till, where the outbox may hold unsynced sales.
   */
  async clearShopData(): Promise<void> {
    const tables = this.tables.filter((table) => table.name !== "auth");
    await this.transaction("rw", tables, async () => {
      await Promise.all(tables.map((table) => table.clear()));
    });
  }

  /** How many operations are waiting to reach the server. Drives the UI counter. */
  async pendingOperationCount(): Promise<number> {
    return this.outbox.where("state").anyOf("LOCAL", "PENDING", "SYNCING").count();
  }

  /** Operations that exhausted their retries and need a human. Never discarded. */
  async deadLetterCount(): Promise<number> {
    return this.outbox.where("state").anyOf("FAILED", "CONFLICT").count();
  }
}

export const db = new SmartSpazaDatabase();

/**
 * Read the sync bookkeeping row, creating it on first run.
 *
 * The device identifier stays null until Phase 1 pairs the terminal; everything
 * else is safe to initialise locally.
 */
export async function getSyncMeta(): Promise<SyncMeta> {
  const existing = await db.syncMeta.get("current");
  if (existing) return existing;

  const fresh: SyncMeta = {
    key: "current",
    deviceId: null,
    cursors: {},
    lastSuccessfulSyncAt: null,
    lastAttemptAt: null,
    receiptSequence: 1,
    operationSequence: 1,
    clockSkewMs: 0,
    schemaVersion: SCHEMA_VERSION,
  };
  await db.syncMeta.put(fresh);
  return fresh;
}

export async function updateSyncMeta(patch: Partial<Omit<SyncMeta, "key">>): Promise<SyncMeta> {
  const current = await getSyncMeta();
  const next: SyncMeta = { ...current, ...patch, key: "current" };
  await db.syncMeta.put(next);
  return next;
}
